// src/hooks/useBiometric.js
import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

// Static import — plugin has built-in web fallback via registerPlugin
let NativeBiometric = null;
try {
  // eslint-disable-next-line
  const mod = require('@capgo/capacitor-native-biometric');
  NativeBiometric = mod.NativeBiometric;
} catch (e) {
  console.warn('Biometric plugin import failed:', e.message);
}

const BIOMETRIC_SERVER = 'com.soldikeeper.app';
const BIOMETRIC_ENABLED_KEY = 'biometricEnabled';

export const useBiometric = () => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [biometryType, setBiometryType] = useState(null); // 'fingerprint' | 'face' | 'iris'
  const [checking, setChecking] = useState(true);

  const isNative = Capacitor.isNativePlatform();

  // Check biometric availability on mount
  useEffect(() => {
    const check = async () => {
      if (!isNative || !NativeBiometric) {
        setChecking(false);
        return;
      }

      try {
        const result = await NativeBiometric.isAvailable();
        setIsAvailable(true);

        switch (result.biometryType) {
          case 1: setBiometryType('fingerprint'); break;
          case 2: setBiometryType('face'); break;
          case 3: setBiometryType('iris'); break;
          default: setBiometryType('fingerprint');
        }
      } catch (e) {
        console.log('Biometric not available:', e.message);
        setIsAvailable(false);
      }

      const enabled = localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
      setIsEnabled(enabled);
      setChecking(false);
    };

    const timeoutId = setTimeout(() => {
      setChecking(false);
    }, 3000);

    check().catch(() => setChecking(false)).finally(() => clearTimeout(timeoutId));
  }, [isNative]);

  const enrollBiometric = useCallback(async (email, password) => {
    if (!NativeBiometric) throw new Error('Biometric plugin not available');

    await NativeBiometric.setCredentials({
      username: email,
      password: password,
      server: BIOMETRIC_SERVER,
    });

    localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
    setIsEnabled(true);
    return true;
  }, []);

  const authenticateWithBiometric = useCallback(async (reason) => {
    if (!NativeBiometric) throw new Error('Biometric plugin not available');

    await NativeBiometric.verifyIdentity({
      reason: reason || 'Log in to SoldiKeeper',
      title: 'Biometric Login',
      subtitle: 'Verify your identity',
      description: 'Use your fingerprint or face to sign in',
      maxAttempts: 3,
    });

    const credentials = await NativeBiometric.getCredentials({
      server: BIOMETRIC_SERVER,
    });

    return {
      email: credentials.username,
      password: credentials.password,
    };
  }, []);

  const disableBiometric = useCallback(async () => {
    if (NativeBiometric) {
      try {
        await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
      } catch (e) {
        console.warn('Error deleting biometric credentials:', e.message);
      }
    }
    localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
    setIsEnabled(false);
  }, []);

  return {
    isNative,
    isAvailable,     // Hardware supports biometric
    isEnabled,       // User has enrolled biometric login
    biometryType,    // 'fingerprint' | 'face' | 'iris'
    checking,        // Still checking availability
    enrollBiometric, // (email, password) => Promise<true>
    authenticateWithBiometric, // (reason?) => Promise<{email, password}>
    disableBiometric, // () => Promise<void>
  };
};

export default useBiometric;
